<template>
  <aside class="right-sidebar">
    <div class="sidebar-content">
      <!-- Admin Overview -->
      <div class="sidebar-section">
        <h3 class="sidebar-title">
          <v-icon size="16" class="mr-1" color="#EA5545">mdi-shield-account</v-icon>
          {{ t('menus.rightSidebar.admin.overviewTitle') }}
        </h3>
        <p class="sidebar-text">
          {{ t('menus.rightSidebar.admin.overviewText') }}
        </p>

        <div
          class="sidebar-action-card mt-2"
          role="button"
          tabindex="0"
          @click="goToAdmin"
          @keydown.enter="goToAdmin"
        >
          <v-icon size="16" class="action-icon">mdi-view-dashboard</v-icon>
          <span class="sidebar-btn-label">
            {{ t('menus.rightSidebar.admin.overviewButton') }}
          </span>
        </div>
        <div
          v-if="canSearchCustomers"
          class="sidebar-action-card mt-2"
          role="button"
          tabindex="0"
          @click="openCustomerSearch"
          @keydown.enter="openCustomerSearch"
        >
          <v-icon size="16" class="action-icon">mdi-account-search</v-icon>
          <span class="sidebar-btn-label">
            {{ t('menus.rightSidebar.admin.customerSearch.button') }}
          </span>
        </div>
        <div
          class="sidebar-action-card mt-2"
          role="button"
          tabindex="0"
          @click="openSupportSearch"
          @keydown.enter="openSupportSearch"
        >
          <v-icon size="16" class="action-icon">mdi-lifebuoy</v-icon>
          <span class="sidebar-btn-label">
            {{ t('menus.rightSidebar.admin.supportSearch.button') }}
          </span>
        </div>
        <div
          class="sidebar-action-card mt-2"
          role="button"
          tabindex="0"
          @click="goToCreateCustomer"
          @keydown.enter="goToCreateCustomer"
        >
          <v-icon size="16" class="action-icon">mdi-account-multiple-plus</v-icon>
          <span class="sidebar-btn-label">
            {{ t('menus.rightSidebar.admin.createCustomerButton') }}
          </span>
        </div>
        <div
          v-if="isSuperAdmin"
          class="sidebar-action-card mt-2"
          role="button"
          tabindex="0"
          @click="goToMailTemplates"
          @keydown.enter="goToMailTemplates"
        >
          <v-icon size="16" class="action-icon">mdi-email-edit</v-icon>
          <span class="sidebar-btn-label">
            {{ t('menus.rightSidebar.admin.mailTemplatesButton') }}
          </span>
        </div>
      </div>

      <v-divider class="my-4"></v-divider>

      <!-- Quick Links -->
      <div class="sidebar-section">
        <h3 class="sidebar-title">{{ t('menus.rightSidebar.admin.linksTitle') }}</h3>
        <div class="sidebar-links">
          <router-link
            to="/admin"
            class="sidebar-link"
          >
            <v-icon size="16" class="mr-2">mdi-ticket-account</v-icon>
            {{ t('menus.rightSidebar.admin.linksCompany') }}
          </router-link>
          <router-link
            to="/admin/create-customer"
            class="sidebar-link"
          >
            <v-icon size="16" class="mr-2">mdi-account-multiple-plus</v-icon>
            {{ t('menus.rightSidebar.admin.linksCreateCustomer') }}
          </router-link>
          <!-- <router-link
            to="/support"
            class="sidebar-link"
          >
            <v-icon size="16" class="mr-2">mdi-ticket</v-icon>
            {{ t('menus.rightSidebar.admin.linksPersonal') }}
          </router-link> -->
        </div>
      </div>

      <v-divider class="my-4"></v-divider>

      <!-- Status -->
      <div class="sidebar-section">
        <h3 class="sidebar-title">{{ t('menus.rightSidebar.admin.statusTitle') }}</h3>
        <div class="status-indicator">
          <v-icon size="12" color="#4caf50" class="mr-2">mdi-circle</v-icon>
          <span class="status-text">{{ t('menus.rightSidebar.admin.statusText') }}</span>
        </div>
      </div>

      <v-dialog
        v-model="showCustomerSearch"
        max-width="720"
      >
        <v-card>
          <v-card-title class="dialog-title">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="#EA5545">mdi-account-search</v-icon>
              <span>{{ t('menus.rightSidebar.admin.customerSearch.title') }}</span>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon variant="text" @click="showCustomerSearch = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <p class="dialog-hint">
              {{ t('menus.rightSidebar.admin.customerSearch.hint') }}
            </p>
            <div class="search-row">
              <v-text-field
                v-model="searchQuery"
                :label="t('menus.rightSidebar.admin.customerSearch.label')"
                variant="outlined"
                density="comfortable"
                @keyup.enter="performSearch"
                :disabled="isSearching"
              />
              <v-btn
                color="#EA5545"
                variant="flat"
                class="ml-3"
                :loading="isSearching"
                :disabled="searchQuery.trim().length < 2 || isSearching"
                @click="performSearch"
              >
                {{ t('menus.rightSidebar.admin.customerSearch.searchButton') }}
              </v-btn>
            </div>
            <div v-if="searchError" class="search-error">
              {{ searchError }}
            </div>
            <div v-if="searchResults.length" class="search-results">
              <v-list density="comfortable">
                <v-list-item
                  v-for="user in searchResults"
                  :key="user.user_id"
                  @click="openUserDetail(user.user_id)"
                  class="search-result-item"
                >
                  <div class="result-main">
                    <strong>{{ user.user_name }}</strong>
                    <span class="result-email">{{ user.user_email }}</span>
                  </div>
                  <v-chip
                    size="x-small"
                    :color="Number(user.user_status) === 10 ? 'success' : 'warning'"
                    variant="flat"
                  >
                    {{ Number(user.user_status) === 10
                      ? t('menus.rightSidebar.admin.customerSearch.statusActive')
                      : t('menus.rightSidebar.admin.customerSearch.statusPending') }}
                  </v-chip>
                </v-list-item>
              </v-list>
            </div>
            <p
              v-else-if="searchTouched && !isSearching"
              class="search-empty"
            >
              {{ t('menus.rightSidebar.admin.customerSearch.empty') }}
            </p>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="showSupportSearch"
        max-width="900"
      >
        <v-card>
          <v-card-title class="dialog-title">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="#EA5545">mdi-lifebuoy</v-icon>
              <span>{{ t('menus.rightSidebar.admin.supportSearch.title') }}</span>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon variant="text" @click="showSupportSearch = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <p class="dialog-hint">
              {{ t('menus.rightSidebar.admin.supportSearch.hint') }}
            </p>
            <div class="search-row">
              <v-text-field
                v-model="supportSearchQuery"
                :label="t('menus.rightSidebar.admin.supportSearch.label')"
                variant="outlined"
                density="comfortable"
                @keyup.enter="performSupportSearch"
                :disabled="isSupportSearching"
              />
              <v-btn
                color="#EA5545"
                variant="flat"
                class="ml-3"
                :loading="isSupportSearching"
                :disabled="supportSearchQuery.trim().length < 2 || isSupportSearching"
                @click="performSupportSearch"
              >
                {{ t('menus.rightSidebar.admin.supportSearch.searchButton') }}
              </v-btn>
            </div>
            <div v-if="supportSearchError" class="search-error">
              {{ supportSearchError }}
            </div>
            <div v-if="isSupportSearching" class="search-loading">
              <v-progress-circular indeterminate color="#EA5545" size="32" />
            </div>
            <div v-if="supportResults.length" class="support-results-table">
              <v-table density="comfortable">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{{ t('menus.rightSidebar.admin.supportSearch.columns.title') }}</th>
                    <th>{{ t('menus.rightSidebar.admin.supportSearch.columns.user') }}</th>
                    <th>{{ t('menus.rightSidebar.admin.supportSearch.columns.created') }}</th>
                    <th>{{ t('menus.rightSidebar.admin.supportSearch.columns.status') }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="support in supportResults" :key="support.sup_id">
                    <td class="support-id">#{{ support.sup_id }}</td>
                    <td>
                      <div class="support-title">{{ support.sup_title || t('adminUserDetail.sections.supports.untitled') }}</div>
                      <div class="support-meta-line" v-if="support.prod_name || support.myp_vertrags_nummer">
                        <v-icon size="14" color="#EA5545" class="mr-1">mdi-package-variant</v-icon>
                        <span>{{ support.prod_name || support.myp_vertrags_nummer }}</span>
                      </div>
                    </td>
                    <td>
                      <div class="support-user">{{ support.user_name || '–' }}</div>
                      <div class="support-email">{{ support.user_email }}</div>
                    </td>
                    <td>{{ formatSupportDate(support.sup_created_date) }}</td>
                    <td>
                      <v-chip
                        size="small"
                        :style="{ backgroundColor: getSupportStatusColor(support.sup_status), color: '#fff' }"
                      >
                        {{ getSupportStatusLabel(support.sup_status) }}
                      </v-chip>
                    </td>
                    <td class="text-right">
                      <v-btn
                        variant="text"
                        color="#EA5545"
                        size="small"
                        @click="openSupportDetail(support.sup_id)"
                      >
                        <v-icon left size="16">mdi-open-in-new</v-icon>
                        {{ t('menus.rightSidebar.admin.supportSearch.view') }}
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </div>
            <p
              v-else-if="supportSearchTouched && !isSupportSearching"
              class="search-empty"
            >
              {{ t('menus.rightSidebar.admin.supportSearch.empty') }}
            </p>
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
  </aside>
</template>

<script setup>
import { computed, inject, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useLanguage } from '@/composables/useLanguage'
import { useUserStore } from '@/stores/userStore'

const router = useRouter()
const { t } = useLanguage()
const baseCalls = inject('baseCalls')
const errorStore = inject('errorStore')
const userStore = useUserStore()

const showCustomerSearch = ref(false)
const searchQuery = ref('')
const searchResults = ref([])
const searchTouched = ref(false)
const isSearching = ref(false)
const searchError = ref('')
const showSupportSearch = ref(false)
const supportSearchQuery = ref('')
const supportResults = ref([])
const supportSearchTouched = ref(false)
const isSupportSearching = ref(false)
const supportSearchError = ref('')
const isSuperAdmin = computed(() => Number(userStore.userData?.user_super_admin ?? 0) === 1)
const canSearchCustomers = computed(() => {
  return Number(userStore.userData?.user_company_id ?? 0) === 1
})

function goToAdmin() {
  router.push({ name: 'adminindex' })
}

function openCustomerSearch() {
  showCustomerSearch.value = true
  searchQuery.value = ''
  searchResults.value = []
  searchTouched.value = false
  searchError.value = ''
}

function openSupportSearch() {
  showSupportSearch.value = true
  supportSearchQuery.value = ''
  supportResults.value = []
  supportSearchTouched.value = false
  supportSearchError.value = ''
}

function goToCreateCustomer() {
  router.push({ name: 'admincreatecustomer' })
}

function goToMailTemplates() {
  router.push({ name: 'adminmailtemplates' })
}

async function performSearch() {
  if (searchQuery.value.trim().length < 2) {
    searchError.value = t('menus.rightSidebar.admin.customerSearch.minChars')
    return
  }
  searchError.value = ''
  searchTouched.value = true
  isSearching.value = true
  try {
    const response = await baseCalls.userPost({
      action: 'searchcompanyusers',
      query: searchQuery.value.trim()
    })
    if (response.data?.success) {
      searchResults.value = response.data.users || []
    } else {
      searchResults.value = []
      const message = response.data?.message || t('menus.rightSidebar.admin.customerSearch.error')
      searchError.value = message
    }
  } catch (error) {
    console.error('performSearch', error)
    searchResults.value = []
    searchError.value = t('menus.rightSidebar.admin.customerSearch.error')
    errorStore?.setError(true, t('menus.rightSidebar.admin.customerSearch.error'))
  } finally {
    isSearching.value = false
  }
}

function openUserDetail(userId) {
  showCustomerSearch.value = false
  router.push({ name: 'adminuserdetail', params: { id: userId } })
}

async function performSupportSearch() {
  if (supportSearchQuery.value.trim().length < 2) {
    supportSearchError.value = t('menus.rightSidebar.admin.supportSearch.minChars')
    return
  }

  supportSearchError.value = ''
  supportSearchTouched.value = true
  isSupportSearching.value = true

  try {
    const response = await baseCalls.userPost({
      action: 'searchcompanysupports',
      query: supportSearchQuery.value.trim()
    })
    if (response.data?.success) {
      supportResults.value = response.data.supports || []
    } else {
      supportResults.value = []
      const message = response.data?.message || t('menus.rightSidebar.admin.supportSearch.error')
      supportSearchError.value = message
    }
  } catch (error) {
    console.error('performSupportSearch', error)
    supportResults.value = []
    supportSearchError.value = t('menus.rightSidebar.admin.supportSearch.error')
    errorStore?.setError(true, t('menus.rightSidebar.admin.supportSearch.error'))
  } finally {
    isSupportSearching.value = false
  }
}

function openSupportDetail(supportId) {
  showSupportSearch.value = false
  router.push({ name: 'adminsupport', params: { id: supportId } })
}

const supportStatusMap = {
  0: { label: 'adminIndex.status.open', color: '#FB8C00' },
  1: { label: 'adminIndex.status.done', color: '#4CAF50' },
  2: { label: 'adminIndex.status.closed', color: '#9E9E9E' },
  3: { label: 'adminIndex.status.urgent', color: '#E53935' }
}

function getSupportStatusLabel(status) {
  const mapEntry = supportStatusMap[Number(status)] || supportStatusMap[0]
  return t(mapEntry.label)
}

function getSupportStatusColor(status) {
  const mapEntry = supportStatusMap[Number(status)] || supportStatusMap[0]
  return mapEntry.color
}

function formatSupportDate(value) {
  if (!value) {
    return '–'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}
</script>

<style scoped src="@/assets/styles/sfc/components/modules/RightSidebarAdmin.css"></style>
